import { useCallback, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { WheelEvent as ReactWheelEvent } from 'react';
import { TABLE_ZOOM, centreOffset, claimsSideways, framingCamera, keepInView, liftOffset, maxPan, minTableHeight, pageCamera, tableHeight } from './tableFit';
import type { Bounds, Origin, Span } from './tableFit';
import { useCamera } from './useCamera';

const noop = () => {};
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));

/**
 * The canvas as a table on a page: a bounded surface as tall as what is placed, at the zoom it
 * opens at, until full canvas hands it a camera of its own.
 *
 * In the page state the camera is derived from what is placed, so a stored position is never
 * touched and a vertical wheel is left for the page to scroll. The returned [cam] has the
 * camera's shape, with panning and the wheel's pan turned off there; full canvas is the plain
 * camera. [slot] goes on the box that holds the table in the page: its width and top are what the
 * table's height rule reads.
 *
 * What the table is fitted to — the top-left of what is placed, and how much it takes up — is
 * latched once [ready] and held while [pageKey] stays: moving the card that is top-left-most, or
 * letting one go beyond it, moves only that card. Where what is placed is smaller than the table it
 * is centred in it, by that latched size.
 */
export function useTable(bounds: Bounds | null, pageKey: string, ready = true) {
  const free = useCamera({ x: 0, y: 0, zoom: 1 }, TABLE_ZOOM);
  const [full, setFullState] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState(0);
  const slot = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState({ width: 0, top: 0 });
  const [, resized] = useReducer((n: number) => n + 1, 0);
  /** Drawn again once the table's box has stopped moving, for whatever is placed against it. */
  const [, settle] = useReducer((n: number) => n + 1, 0);

  useLayoutEffect(() => {
    window.addEventListener('resize', resized);
    return () => window.removeEventListener('resize', resized);
  }, []);
  useLayoutEffect(() => {
    const el = slot.current;
    if (!el) return;
    const page = el.offsetParent?.getBoundingClientRect().top ?? 0;
    const next = { width: el.offsetWidth, top: page + el.offsetTop };
    setFrame((was) => (was.width === next.width && was.top === next.top ? was : next));
  });

  const held = useRef<{ key: string; origin: Origin; span: Span | null } | null>(null);
  if (held.current?.key !== pageKey) held.current = null;
  if (!held.current && ready) {
    held.current = {
      key: pageKey,
      origin: bounds ? { x: bounds.minX, y: bounds.minY } : { x: 0, y: 0 },
      span: bounds ? { w: bounds.maxX - bounds.minX, h: bounds.maxY - bounds.minY } : null,
    };
  }
  const origin = held.current?.origin ?? (bounds ? { x: bounds.minX, y: bounds.minY } : null);
  const span = held.current?.span ?? null;
  const originKey = origin && `${origin.x},${origin.y}`;

  const min = minTableHeight(window.innerHeight, frame.top);
  const lift = liftOffset(span, zoom, min);
  const centre = centreOffset(span, zoom, frame.width);
  const height = tableHeight(bounds, origin, zoom, min, lift);
  const reach = maxPan(bounds, origin, zoom, frame.width);
  const panned = clamp(pan, 0, reach);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- [origin] is tracked by its key
  const page = useMemo(() => pageCamera(origin, zoom, panned, centre, lift), [originKey, zoom, panned, centre, lift]);

  const setFull = useCallback(
    (on: boolean) => {
      if (on) {
        const main = document.querySelector('.nook-main')?.getBoundingClientRect();
        free.setCamera(framingCamera(bounds, { w: main?.width ?? window.innerWidth, h: main?.height ?? window.innerHeight }, TABLE_ZOOM));
      }
      setFullState(on);
    },
    [free, bounds],
  );

  const onWheel = useCallback(
    (e: ReactWheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        setZoom((z) => clamp(z * (1 - e.deltaY / 400), TABLE_ZOOM.min, TABLE_ZOOM.max));
      } else if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        setPan(clamp(panned + e.deltaX, 0, reach));
      }
    },
    [panned, reach],
  );

  const cam = useMemo(
    () =>
      full
        ? free
        : {
            ...free,
            camera: page,
            onWheel,
            beginPan: noop,
            onPointerMove: noop,
            endPan: noop,
            wantsPan: () => false,
            isPanning: () => false,
            zoomBy: noop,
            zoomAt: noop,
            toCanvas: (sx: number, sy: number, rect: DOMRect) => ({
              x: (sx - rect.left - page.x) / page.zoom,
              y: (sy - rect.top - page.y) / page.zoom,
            }),
          },
    [full, free, page, onWheel],
  );

  const zoomBy = useCallback(
    (factor: number) => {
      const box = slot.current?.querySelector('.table')?.getBoundingClientRect();
      if (full && box) free.zoomAt(box.width / 2, box.height / 2, factor);
      else if (full) free.zoomBy(factor);
      else setZoom((z) => clamp(z * factor, TABLE_ZOOM.min, TABLE_ZOOM.max));
    },
    [full, free],
  );

  return {
    cam,
    full,
    setFull,
    zoomBy,
    /** How far the page state scrolls sideways, and where it is. */
    reach: { pan: panned, max: reach, width: frame.width },
    claimsX: claimsSideways(full, panned, bounds, free.camera),
    panTo: (to: number) => setPan(clamp(to, 0, reach)),
    percent: Math.round((full ? free.camera.zoom : zoom) * 100),
    height,
    slot,
    settle,
    keepInView: (at: { x: number; y: number }) => keepInView(at, full ? null : origin, zoom, centre, lift),
  };
}

export type TableState = ReturnType<typeof useTable>;
